import { FC, Key } from 'react';
import { Col, Row, Tree } from 'antd';
import type { AntTreeNodeProps, DataNode, TreeProps } from 'antd/es/tree';
import { TbChevronDown } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { OverflowFadeScrollArea, SmallTag } from '@/shared';

import { countGroupPolicies, ROLE_POLICY_SECTIONS, RolePolicyGroup } from './rolePolicies';

interface RolePolicyTreeProps {
    checkedKeys: Key[];
    maxHeight?: number | string;
    minHeight?: number | string;
    readOnly?: boolean;
    onCheck?: TreeProps['onCheck'];
}

function buildRolePolicyTreeData(): DataNode[] {
    const buildGroupNode = (sectionKey: string, group: RolePolicyGroup, parentKeys: string[] = []): DataNode => {
        const groupPath = [...parentKeys, group.key];

        return {
            key: `${sectionKey}:${groupPath.join(':')}`,
            title: (
                <Row align='middle' wrap={false}>
                    <Col flex='none'>{group.label}</Col>
                    <Col flex='none'>
                        <SmallTag message={countGroupPolicies(group)} styles={{ marginLeft: '0.375rem' }} />
                    </Col>
                </Row>
            ),
            children: [
                ...group.policies.map((item) => ({ key: item.key, title: item.label })),
                ...(group.children ?? []).map((child) => buildGroupNode(sectionKey, child, groupPath)),
            ],
        };
    };

    return ROLE_POLICY_SECTIONS.map((section) => ({
        key: section.key,
        title: (
            <Row align='middle' wrap={false}>
                <Col flex='none'>{section.label}</Col>
                <Col flex='none'>
                    <SmallTag
                        message={section.groups.reduce((count, group) => count + countGroupPolicies(group), 0)}
                        styles={{ marginLeft: '0.375rem' }}
                    />
                </Col>
            </Row>
        ),
        children: section.groups.map((group) => buildGroupNode(section.key, group)),
    }));
}

const rolePolicyTreeData = buildRolePolicyTreeData();

const RolePolicyTree: FC<RolePolicyTreeProps> = ({
    checkedKeys,
    maxHeight = '25rem',
    minHeight,
    readOnly = false,
    onCheck,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <OverflowFadeScrollArea
            aria-label='Role policies'
            containerStyle={{
                maxHeight,
                minHeight,
                border: `1px dashed ${token.colorBorder}`,
                borderRadius: token.borderRadius,
            }}
            style={{ padding: '0.75rem 0.5rem' }}
            surface='container'
        >
            <Tree
                checkable
                defaultExpandAll
                selectable={false}
                showLine
                switcherIcon={(props: AntTreeNodeProps) => (
                    <TbChevronDown
                        style={{
                            transform: props.expanded ? undefined : 'rotate(-90deg)',
                            color: token.colorTextTertiary,
                        }}
                    />
                )}
                treeData={rolePolicyTreeData}
                checkedKeys={checkedKeys}
                onCheck={readOnly ? () => undefined : onCheck}
            />
        </OverflowFadeScrollArea>
    );
};

export default RolePolicyTree;
